import React, { FC, useRef, useEffect, useState, memo } from 'react';
import { GameSegmentType } from '../../types';
import { GameKConfig } from '../../_config/constant';
import levelS from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-s.svg';
import levelA from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-a.svg';
import levelB from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-b.svg';
import levelC from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-c.svg';
import levelD from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/level-d.svg';
import { get } from 'lodash-es';
import * as eCharts from 'echarts';
import { useIntl, getLocale } from 'umi';

import './Distribution.scss';

interface DistributionProps {
  gameList: any[];
  gameSegment: GameSegmentType;
  isAllGame: boolean;
}

const XProportion = 70;
const YProportion = 100;

const Distribution: FC<DistributionProps> = (props) => {
  let myChart: any = null;
  const chartRef = useRef(null);
  const { gameList, gameSegment, isAllGame } = props;
  const [options, setOptions] = useState<any>();
  const { formatMessage } = useIntl();
  const locale = getLocale();
  // 反比例函数曲线图
  const getLineData = (k: number, min: number, max: number) => {
    let list = [];
    for (let i = min; i <= max; i = i + 0.1) {
      list.push([i, (k / i).toFixed(2)]);
    }
    return list;
  };

  const getFunLineData = (kList: any[]) =>
    kList?.map((item, index) => ({
      name: item?.value,
      type: 'line',
      symbol: 'none',
      smooth: true,
      data: getLineData(item.value, item.min, item.max),
      lineStyle: {
        opacity: 0.9,
        color: '#E4E4E4',
        type: 'dotted',
      },
      emphasis: {
        lineStyle: {
          color: '#333',
          opacity: 0.3,
        },
      },
    }));

  const getLevelImage = (type: string) => {
    switch (type) {
      case 'S':
        return levelS;
      case 'A':
        return levelA;
      case 'B':
        return levelB;
      case 'C':
        return levelC;
      case 'D':
        return levelD;
      default:
        return '';
    }
  };

  // 获取对应的等级名称
  const getLevelName = (index: number) => {
    switch (index) {
      case 0:
        return 'D';
      case 1:
        return 'C';
      case 2:
        return 'B';
      case 3:
        return 'A';
      case 4:
        return 'S';
      default:
        return '';
    }
  };

  // 自定义tooltip函数
  const gameTooltipFormatter = (
    item: any,
  ) => `<div class="distribution-chart-tooltip">
    <div class="distribution-chart-tooltip-title">
      <img
        src="${item?.cover}"
        width="24px"
        height="24px"
        alt=""
      />
      <span class="distribution-chart-tooltip-title-name">
        ${item?.name}
      </span>
    </div>
    <div class="distribution-chart-tooltip-item">
      <span class="distribution-chart-tooltip-item-label">Lifetime</span>
      <span>Level</span>
    </div>
    <div class="distribution-chart-tooltip-item">
      <span class="distribution-chart-tooltip-item-label">ARPMAU</span>
      <span class="distribution-chart-tooltip-item-score">
        <img src="${getLevelImage(item?.arpu_rate)}" alt="" width="12px" />
        <span>${item?.arpu_rate}</span>
      </span>
    </div>
    <div class="distribution-chart-tooltip-item">
      <span class="distribution-chart-tooltip-item-label">
        Retention
      </span>
      <span class="distribution-chart-tooltip-item-score">
        <img src="${getLevelImage(item?.lty_rate)}" alt="" width="12px" />
        <span>${item?.lty_rate}</span>
      </span>
    </div>
  </div>`;

  // <span class="item-score">${item?.arpu?.toFixed(
  //   gameSegment === 'casual' ? 2 : 1,
  // )}</span>
  // <span class="item-score">${item?.lty?.toFixed(1) || ''}</span>;

  useEffect(() => {
    // 游戏散点图数据
    let gameListSeries: any[] = [];
    // x轴坐标评级点
    let xAxisList: any[] = [];
    // y轴坐标评级点
    let yAxisList: any[] = [];
    let gassPoint: any = {};
    const config = GameKConfig?.[gameSegment] || {};

    // 游戏目标数据
    let targetGameList: any[] = [];
    if (isAllGame) {
      //Put in all games 开关 开 如果游戏大于20 散点图插入所有图片
      targetGameList = gameList;
    } else {
      //Put in all games 开关 关
      if (gameList?.length >= 20) {
        // 如果游戏大于20 散点图插入列表前20图片
        targetGameList = gameList?.slice(0, 20);
      } else {
        // 放默认大于20的图片
        targetGameList = gameList;
      }
    }
    // ### 步骤1、 遍历游戏series 散点图数据
    targetGameList?.forEach((item) => {
      gameListSeries.push({
        name: item?.name,
        data: [[item?.lty, item?.arpu]], // 二维坐标点 lty为x轴坐标 arpu为Y轴坐标
        symbolSize: 24, // 图片的大小
        cursor: 'pointer',
        type: 'scatter',
        symbol: `image://${item?.cover}`,
        symbolOffset: ['50%', '-50%'], // 相对于自身偏移
        z: 3,
        emphasis: {
          scale: 1.5,
        },
        tooltip: {
          formatter: gameTooltipFormatter(item),
          extraCssText: 'border: none;',
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        // labelLayout: {
        //   hideOverlap: true,
        //   moveOverlap:{
        //     shiftX: 4,
        //     shiftY: 6
        //   }
        // }
        // symbol 设置为image后不起作用
        // emphasis: {
        //   itemStyle: {
        //     borderWidth: 4,
        //     borderColor: '#000',
        //   },
        // },
      });
      xAxisList.push(item?.lty);
      yAxisList.push(item?.arpu);
    });
    // ### 步骤2、取x轴的最大值
    // 取游戏列表lty的最大值
    const xDataAxisMax = Math.ceil(Math.max(...xAxisList));
    // 取x轴S评级分割点的最大值
    const xSlitSMax = config?.x?.[config?.x?.length - 1] || 0;
    // 如果游戏列表数据lty最大值大于 S级x轴的分割线的值 则取最大值
    // 如果游戏列表数据lty最大值小于等于 S级x轴的分割线的值 则取S级分割值 +1 , +1为了更好标记S
    const xAxisMax = xDataAxisMax > xSlitSMax ? xDataAxisMax : xSlitSMax + 1;

    // 假设当前y轴分割点为d1<d2<d3<d4
    // 所选各游戏中最大值为 ymax > d4
    // ### 步骤3、取Y轴的最大值

    // ### 步骤3、取游戏列表arpu的最大值
    const arpuList = gameList.map((item) => item?.arpu);
    const yDataAxisMax = Math.max(...arpuList);

    // 取y轴S评级分割点的最大值
    const ySplitSMax = config?.y?.[config?.y?.length - 1] || 0;
    let yAxisMax = 0;

    //
    if (yDataAxisMax <= ySplitSMax) {
      // 3.1. 若ymax<d4 去d4+1; +1作用是为了更好处理边界问题
      yAxisMax = ySplitSMax + 1;
    } else if (yDataAxisMax > ySplitSMax && yDataAxisMax < 1.5 * ySplitSMax) {
      // 3.2 若 d4<ymax< 1.5*d4，则以ymax进行缩放
      yAxisMax = Math.ceil(yDataAxisMax) + 1;
    } else {
      // 3.3 ymax > 1.5*d4 对具体y进行缩放处理
      yAxisMax = Math.ceil(1.5 * ySplitSMax) + 1;
      gameListSeries = gameListSeries?.map((item) => {
        let newData: any = [];
        // 获取x轴坐标
        const xPointData = get(item, 'data.0.0', 0);
        // 获取Y轴坐标
        const yPointData = get(item, 'data.0.1', 0);
        if (yPointData > ySplitSMax) {
          // 若 ymax> d4  d4*[1+0.5*(y-d4)/(ymax-d4)]
          newData = [
            [
              xPointData,
              ySplitSMax *
                (1 +
                  (0.5 * (yPointData - ySplitSMax)) /
                    (yDataAxisMax - ySplitSMax)),
            ],
          ];
        } else {
          // y<d4 y值取原值
          newData = item?.data;
        }
        return {
          ...item,
          data: newData,
        };
      });
    }

    // ### 步骤4、绘制反比例函数 弧线图数据
    const lineSource = config?.k?.map((item: number) => ({
      value: item, // 反比例函数K值
      min: Number((item / yAxisMax).toFixed(3)), // 横坐轴起点
      max: xAxisMax, // 横轴终点
    }));

    // ### 步骤5、 绘制反比例函数 弧线图数据
    const inverseListSeries = getFunLineData(lineSource);

    // ### 步骤6、绘制等级区域名称
    const tierSeriesList = config?.x?.map((item: number, index: number) => {
      let xPoint = 0;
      if (index === config?.x?.length - 1) {
        // T1名称的位置lineSource?.[index]?.min 和lineSource?.[index]?.min +1 之间的中点
        xPoint = (lineSource?.[index]?.min * 2 + 1) / 2;
      } else {
        // 两曲线之间的中点
        xPoint = (lineSource?.[index + 1]?.min + lineSource?.[index]?.min) / 2;
      }
      return {
        name: `T${config?.x?.length - index}`,
        data: [[xPoint, yAxisMax]], // y轴位置都Y轴的最大值位置
        symbolSize: 0,
        type: 'scatter',
        symbol: 'circle',
        hoverAnimation: false,
        cursor: 'default',
        label: {
          show: true,
          position: 'bottom',
          formatter: '{a}',
          color: '#838697',
          fontWeight: 400,
        },
        tooltip: {
          trigger: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
      };
    });
    // ### 步骤7、 标记x轴评级s a b c d
    const levelXList = [...config?.x, xAxisMax];
    const levelXSeriesList = levelXList?.map((item: number, index: number) => {
      let xPoint = 0;
      if (index === 0) {
        xPoint = Number((item / 2)?.toFixed(2));
        gassPoint.xdPoint = xPoint;
      } else {
        xPoint = Number(((item + levelXList[index - 1]) / 2)?.toFixed(2));
      }

      if (index === 4) {
        gassPoint.xsPoint = Number(
          ((item + levelXList[index - 1]) / 2)?.toFixed(2),
        );
      }
      const tartName = getLevelName(index);
      const imageX = getLevelImage(tartName);
      return {
        name: tartName,
        data: [[xPoint, 0]],
        symbolSize: 10,
        type: 'scatter',
        symbol: `image://${imageX}`,
        symbolOffset: [-8, -10],
        z: 6,
        hoverAnimation: false,
        cursor: 'default',
        label: {
          show: true,
          color: '#888888',
          position: [13, 1],
          formatter: '{a}',
          fontSize: 12,
        },
        tooltip: {
          // trigger: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0)',
          textStyle: {
            color: '#d8dae4',
          },
          position: 'top',
          formatter: () =>
            `<div style="padding: 10px">${get(
              config,
              `retention.${getLevelName(index)}`,
              '',
            )}</div>`,
        },
      };
    });

    // ### 步骤8 标记Y轴评级s a b c d
    const levelYList = [...config?.y, yAxisMax];
    const levelYSeriesList = levelYList?.map((item: number, index: number) => {
      let yPoint = 0;
      if (index === 0) {
        yPoint = Number((item / 2)?.toFixed(2));
        gassPoint.ydPoint = yPoint;
      } else {
        yPoint = Number(((item + levelYList[index - 1]) / 2)?.toFixed(2));
      }

      if (index === 4) {
        gassPoint.ysPoint = Number(
          ((item + levelYList[index - 1]) / 2)?.toFixed(2),
        );
      }

      const tartName = getLevelName(index);
      const imageY = getLevelImage(tartName);

      return {
        name: getLevelName(index),
        data: [[0, yPoint]],
        symbolSize: 10,
        type: 'scatter',
        symbol: `image://${imageY}`,
        symbolOffset: [12, 0],
        hoverAnimation: false,
        z: 6,
        cursor: 'default',
        label: {
          show: true,
          color: '#888888',
          position: [14, 1],
          formatter: '{a}',
        },
        tooltip: {
          // trigger: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0)',
          position: 'right',
          textStyle: {
            color: '#d8dae4',
          },
          formatter: () =>
            `<div style="padding: 10px">${get(
              config,
              `arpmau.${getLevelName(index)}`,
              '',
            )}</div>`,
        },
      };
    });

    // 9 等级交叉点

    const gaasInfo = [
      {
        name: 'Strong GaaS',
        data: [[gassPoint?.xsPoint, gassPoint?.ysPoint]],
        symbolSize: 0,
        type: 'scatter',
        symbol: 'circle',
        hoverAnimation: false,
        cursor: 'default',
        label: {
          show: true,
          color: '#888888',
          position: [-42, -6],
          formatter: '{a}',
          fontWeight: 600,
        },
        tooltip: {
          trigger: 'none',
        },
      },
      {
        name: 'Weak GaaS',
        data: [[gassPoint?.xdPoint, gassPoint?.ydPoint]],
        symbolSize: 0,
        type: 'scatter',
        symbol: 'circle',
        hoverAnimation: false,
        cursor: 'default',
        label: {
          show: true,
          color: '#838697',
          fontSize: 14,
          fontWeight: 700,
          position: [-30, -6],
          formatter: '{a}',
        },
        tooltip: {
          trigger: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
      },
    ];

    // ### 步骤9 添加x轴刻度线
    const xMarkLinePoint = config?.x?.map((item, index) => {
      return {
        data: [],
        type: 'line',
        smooth: true,
        markLine: {
          lineStyle: {
            width: 1,
            type: 'solid',
            color: '#BFBFBF',
            z: 3,
          },
          label: {
            show: true,
            position: 'start',
            formatter: config?.xTitle?.[index],
            color: '#838697',
            height: 10,
            padding: [2, 0],
            lineHeight: 10,
            borderWidth: 0,
          },
          silent: true, // 鼠标悬停事件, true悬停不会出现实线
          symbol: 'none', // 去掉箭头
          data: [
            [{ coord: [item, 0] }, { coord: [item, yAxisMax / XProportion] }],
          ],
        },
      };
    });

    // ### 步骤10 添加x轴刻度线
    const yMarkLinePoint = config?.y?.map((item, index) => {
      return {
        data: [],
        type: 'line',
        smooth: true,
        markLine: {
          lineStyle: {
            width: 1,
            type: 'solid',
            color: '#BFBFBF',
          },
          label: {
            show: true,
            position: 'start',
            formatter: config?.yTitle?.[index],
            color: '#838697',
            height: 10,
            padding: [2, 0],
            lineHeight: 10,
            borderWidth: 0,
          },
          silent: true, // 鼠标悬停事件, true悬停不会出现实线
          symbol: 'none', // 去掉箭头
          data: [
            [{ coord: [0, item] }, { coord: [xAxisMax / YProportion, item] }],
          ],
        },
      };
    });

    const gameOptions = {
      tooltip: {
        show: true,
        confine: true, // fix遮挡:让tooltip展示在折线区域内
        trigger: 'item',
        showDelay: 10,
        hideDelay: 100,
        borderWidth: 0,
        borderColor: '#fff',
        position: 'right',
        padding: 0,
        backgroundColor: 'rgba(255, 255, 255, 0)',
      },
      graphic: [
        {
          type: 'text',
          right: 24,
          bottom: 0,
          cursor: 'default',
          style: {
            text: formatMessage({ id: 'dbi_benchmark_retention_d30' }),
            fill: '#838697',
            fontWeight: 400,
            fontSize: 14,
          },
        },
        {
          type: 'text',
          right: 44,
          top: 80,
          cursor: 'default',
          style: {
            text: 'Strong GaaS',
            fill: '#838697',
            fontWeight: 700,
            fontSize: 14,
          },
        },
        {
          type: 'text',
          left: 80,
          bottom: 70,
          cursor: 'default',
          style: {
            text: 'Weak GaaS',
            fill: '#838697',
            fontWeight: 700,
            fontSize: 14,
          },
        },
      ],
      grid: {
        top: 30,
        bottom: 40,
        left: 32,
        right: 27,
        containLabel: true,
      },
      xAxis: [
        {
          type: 'value',
          max: xAxisMax,
          splitLine: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              color: '#BFBFBF',
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: false, // 不显示坐标轴上的文字
          },
          boundaryGap: false, // 不留白，从原点开始
        },
      ],
      yAxis: {
        type: 'value',
        name: 'ARPMAU',
        nameGap: 16,
        nameTextStyle: {
          color: '#838697',
          fontWeight: 400,
          fontSize: 14,
          // padding: [0, 48, 0, 0],
        },
        splitLine: {
          show: false,
        },
        max: yAxisMax,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#BFBFBF',
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
      },
      series: [
        ...gameListSeries,
        ...inverseListSeries,
        ...tierSeriesList,
        ...levelXSeriesList,
        ...levelYSeriesList,
        ...xMarkLinePoint,
        ...yMarkLinePoint,
      ],
    };
    setOptions(gameOptions);
  }, [gameList, gameSegment, isAllGame, locale]);

  const handleResize = () => {
    try {
      myChart.resize();
    } catch (err) {}
  };

  useEffect(() => {
    if (myChart === null) {
      myChart = eCharts.init(chartRef!.current!);
    }
    options && myChart.setOption(options, true);
  }, [options]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);

  return (
    <div
      ref={chartRef}
      style={{ height: 430, width: '100%' }}
      className="distribution-chart-container"
    ></div>
  );
};

Distribution.defaultProps = {};

export default memo(Distribution);
